import { css } from "@emotion/react";
import { styled } from "@mui/material/styles";
import FontIcon from "@odl/core/components/DataDisplay/FontIcon";
import FlexBox from "components/FlexBox/FlexBox";
import Dialog from "components/odl-v2/Dialog/Dialog";
import DialogActionCancel from "components/odl-v2/Dialog/DialogActionCancel";
import DialogActionGroup from "components/odl-v2/Dialog/DialogActionGroup";
import DialogActions from "components/odl-v2/Dialog/DialogActions";
import DialogContent from "components/odl-v2/Dialog/DialogContent";
import React from "react";
import { useTranslation } from "react-i18next";
import { usePaginatedParticipantHistory } from "./functions/usePaginatedParticipantHistory";
import { useSortParticipantHistory } from "./functions/useSortParticipantHistory";
import ParticipantHistoryTable from "./ParticipantHistoryTable";

type Props = {
  applicationId: number;
  participantId: number;
  onClose: (created?: boolean) => any;
};

const ParticipantHistoryDialog: React.FC<Props> = ({ applicationId, participantId, onClose }) => {
  const { t } = useTranslation();

  const { sortCriteria, sortDirection, onSortingChange } = useSortParticipantHistory();
  const { hasNextPage, loadNextPage } = usePaginatedParticipantHistory({
    applicationId,
    participantId,
    sortBy: sortCriteria,
    sortDirection: sortDirection,
  });

  return (
    <Dialog
      open={true}
      fullWidth={true}
      maxWidth={"lg"}
      data-testid={"ParticipantHistoryDialog"}
      title={
        <FlexBox alignItems={"center"} spacing={4}>
          <FontIcon name={"i-data-data-view-alt"} />
          {t(`History`)}
        </FlexBox>
      }
      onClose={() => onClose()}
    >
      <StyledDialogContent>
        <ParticipantHistoryTable
          applicationId={applicationId}
          participantId={participantId}
          loadNextPage={loadNextPage}
          sortCriteria={sortCriteria}
          sortDirection={sortDirection}
          hasNextPage={hasNextPage}
          onSortingChange={onSortingChange}
        />
      </StyledDialogContent>

      <DialogActions>
        <DialogActionGroup>
          <DialogActionCancel data-testid="CancelButton" onClick={() => onClose(false)}>
            {t(`Cancel`)}
          </DialogActionCancel>
        </DialogActionGroup>
      </DialogActions>
    </Dialog>
  );
};

const StyledDialogContent = styled(DialogContent)(
  ({ theme }) => css`
    padding: 10px 32px;
    margin: 0;
    min-height: 724px;
    max-height: 724px;
    display: flex;
    flex-direction: column;
  `
);

export default ParticipantHistoryDialog;
